// The telemetry track: the voice's recent shaft states, one per render quantum,
// and the crank angle between them.
//
// The worklet sends one telemetry record per process() call: the context frame
// at entry (R_FRAME), the voice's own sample count (R_VOICE_FRAME) and the shaft
// state at the block's first sample (docs/contracts/audio-bridge.md). The track
// keeps the newest CAPACITY of them in a ring of typed arrays and answers two
// questions without allocating:
//
// - voiceFrameAt(contextFrame): which of the voice's samples the audio context
//   played at a context frame. currentFrame is not a block counter (Chrome
//   skips frames after start and has repeated one), so the answer counts from
//   the newest record at or before the frame, never from a count of calls.
// - sample(voiceFrame, out): the crank angle, its rate and the slow state at a
//   voice frame. Between two records the angle follows a cubic Hermite curve
//   whose tangents are finite differences of the recorded angles, so a constant
//   speed and a steady ramp are reproduced exactly. The record's ω is the
//   shaft's speed in simulated time, which differs from the angle's rate under
//   time_scale or a pause, so it is not used for the curve.
//
// Past the newest record, which is the usual case when the output latency is
// short (the telemetry arrives in batches, about 60 a second), the angle is
// carried forward with the speed and the acceleration of the last three
// records: a steady ramp stays exact. The acceleration is capped at MAX_ACCEL
// and used for at most ACCEL_HORIZON_S, so a step (a pause, a resume, a change
// of time_scale) cannot fling the crank round; the speed never changes sign.
//
// A voice frame that goes backwards means the voice started again (a new init
// or a recovery), and the track starts over.

import {
  CYCLE,
  FLAG_FAILED,
  FLAG_NOT_READY,
  MAX_SLOW,
  R_CYCLE,
  R_FLAGS,
  R_FRAME,
  R_LENGTH,
  R_THETA,
  R_VOICE_FRAME,
  type TelemetryBatch,
} from "../audio/protocol";

/** Records kept: about 2.7 s of 128-frame blocks, far more than any output latency. */
export const CAPACITY = 1024;
/**
 * An angle step between two records larger than this is a jump (a scrub or a
 * restart), not motion: half a revolution in one record is 112,500 rpm at 128 frames.
 */
const JUMP = Math.PI;
/**
 * Two neighbouring chord slopes that differ by more than this share of the
 * larger meet at a corner. A steady ramp changes the slope by well under 1 %
 * per record (4,000 rpm/s at 1,000 rpm: 0.7 %), a stop by 100 %.
 */
const KINK = 0.2;
/** How far past the newest record the angle is carried forward, in seconds; beyond that it holds. */
export const MAX_EXTRAPOLATION_S = 0.25;
/**
 * The largest angular acceleration carried forward, rad/s²: about 19,000 rpm/s,
 * well above a free-revving engine, so only steps of the clock reach it.
 */
export const MAX_ACCEL = 2000;
/** How long the acceleration is carried forward before the speed is held, in seconds. */
export const ACCEL_HORIZON_S = 0.04;

/** The shaft at one voice frame, as sample() writes it. */
export interface TrackSample {
  /** Crank radians in [0, CYCLE). */
  theta: number;
  /** Whole cycles since init, with theta: the unwrapped angle is cycle·CYCLE + theta. */
  cycle: number;
  /** How fast the angle moves, radians per second of audio (0 while paused). */
  rate: number;
  /** The slow state of the block holding the frame, in the order of ready.slow. */
  readonly slow: Float64Array;
  /** How many slow-state values are valid. */
  slowCount: number;
  /** False when the track is empty and nothing was written. */
  valid: boolean;
}

export function newTrackSample(): TrackSample {
  return { theta: 0, cycle: 0, rate: 0, slow: new Float64Array(MAX_SLOW), slowCount: 0, valid: false };
}

export class TelemetryTrack {
  readonly sampleRate: number;
  private readonly frame = new Float64Array(CAPACITY);
  private readonly voice = new Float64Array(CAPACITY);
  private readonly length = new Float64Array(CAPACITY);
  private readonly cyc = new Float64Array(CAPACITY);
  private readonly theta = new Float64Array(CAPACITY);
  private readonly slow = new Float64Array(CAPACITY * MAX_SLOW);
  private slowCount = 0;
  /** Index of the oldest record and the number kept. */
  private head = 0;
  private count = 0;
  /** Starts of the voice seen so far (restarts after the first). */
  restarts = 0;

  constructor(sampleRate: number) {
    this.sampleRate = sampleRate;
  }

  get size(): number {
    return this.count;
  }

  /** The newest record's context and voice frames, or NaN when empty. */
  get newestFrame(): number {
    return this.count === 0 ? Number.NaN : this.frame[this.at(this.count - 1)]!;
  }
  get newestVoiceFrame(): number {
    return this.count === 0 ? Number.NaN : this.voice[this.at(this.count - 1)]!;
  }

  clear(): void {
    this.head = 0;
    this.count = 0;
  }

  /** Takes every record of a batch (call it from AudioEngine.onTelemetry). */
  ingest(batch: TelemetryBatch): void {
    const slowCount = Math.min(batch.slowCount, MAX_SLOW);
    for (let i = 0; i < batch.count; i++) {
      const flags = batch.get(i, R_FLAGS);
      // A block with no voice running carries no angle.
      if ((flags & (FLAG_NOT_READY | FLAG_FAILED)) !== 0) continue;
      this.push(
        batch.get(i, R_FRAME),
        batch.get(i, R_VOICE_FRAME),
        batch.get(i, R_LENGTH),
        batch.get(i, R_CYCLE),
        batch.get(i, R_THETA),
        batch,
        i,
        slowCount,
      );
    }
  }

  /** Adds one record. `slowFrom` and `record` give its slow state (a batch and record index), or null. */
  push(
    frame: number,
    voiceFrame: number,
    length: number,
    cycle: number,
    theta: number,
    slowFrom: TelemetryBatch | null = null,
    record = 0,
    slowCount = 0,
  ): void {
    if (this.count > 0) {
      const last = this.voice[this.at(this.count - 1)]!;
      if (voiceFrame < last) {
        this.restarts++;
        this.clear();
      } else if (voiceFrame === last) {
        return; // the same block again
      }
    }
    if (this.count === CAPACITY) {
      this.head = (this.head + 1) % CAPACITY;
      this.count--;
    }
    const k = this.at(this.count);
    this.frame[k] = frame;
    this.voice[k] = voiceFrame;
    this.length[k] = length;
    this.cyc[k] = cycle;
    this.theta[k] = theta;
    this.slowCount = slowCount;
    for (let s = 0; s < slowCount; s++) this.slow[k * MAX_SLOW + s] = slowFrom!.slow(record, s);
    this.count++;
  }

  /**
   * The voice frame the context played at `contextFrame`: counted from the newest
   * record at or before it. A frame in a skip of currentFrame maps to the end of
   * the block before the skip; a frame before the oldest record maps to that
   * record. NaN when the track is empty.
   */
  voiceFrameAt(contextFrame: number): number {
    if (this.count === 0) return Number.NaN;
    const i = this.lastAtOrBefore(this.frame, contextFrame);
    if (i < 0) return this.voice[this.at(0)]!;
    const k = this.at(i);
    const into = contextFrame - this.frame[k]!;
    // Inside the newest block, and past it until the next batch arrives, the voice runs on.
    if (i === this.count - 1) return this.voice[k]! + into;
    return this.voice[k]! + Math.min(into, this.length[k]!);
  }

  /** Writes the shaft at voice frame `v` into `out`. Returns false (and writes nothing) when empty. */
  sample(v: number, out: TrackSample): boolean {
    if (this.count === 0 || Number.isNaN(v)) {
      out.valid = false;
      return false;
    }
    const n = this.count;
    let i = this.lastAtOrBefore(this.voice, v);
    if (i < 0) i = 0;
    const a = this.at(i);
    out.slowCount = this.slowCount;
    for (let s = 0; s < this.slowCount; s++) out.slow[s] = this.slow[a * MAX_SLOW + s]!;
    out.valid = true;

    const va = this.voice[a]!;
    if (v < va) {
      // Before the oldest record: hold it.
      this.write(out, this.cyc[a]!, this.theta[a]!, 0);
      return true;
    }
    if (i === n - 1) {
      // Past the newest record: carry its speed and acceleration forward a while.
      this.endMotion();
      const dt = Math.min((v - va) / this.sampleRate, MAX_EXTRAPOLATION_S);
      let rate = this.endRate;
      const acc = this.endAccel;
      // Accelerate for at most the horizon, and never through zero speed.
      let ta = Math.min(dt, ACCEL_HORIZON_S);
      if (acc !== 0 && rate * (rate + acc * ta) < 0) ta = -rate / acc;
      const angle = rate * ta + 0.5 * acc * ta * ta;
      rate += acc * ta;
      this.write(out, this.cyc[a]!, this.theta[a]! + angle + rate * (dt - ta), rate);
      return true;
    }

    const b = this.at(i + 1);
    const h = (this.voice[b]! - va) / this.sampleRate;
    const delta = this.step(i);
    const t = (v - va) / (this.voice[b]! - va);
    if (Math.abs(delta) > JUMP) {
      // A jump: hold the angle until the next record.
      this.write(out, this.cyc[a]!, this.theta[a]!, 0);
      return true;
    }
    // Tangents (radians per second): the slopes of the neighbouring chords, so a
    // steady ramp is exact; a segment at either end of the track uses its own chord.
    const chord = delta / h;
    const m0 = i > 0 ? this.tangent(i - 1, i, i + 1, chord) : chord;
    let m1 = chord;
    if (i + 2 < n) {
      m1 = this.tangent(i, i + 1, i + 2, chord);
    } else {
      this.endMotion();
      if (Math.abs(this.endRate - chord) <= KINK * Math.max(Math.abs(this.endRate), Math.abs(chord))) m1 = this.endRate;
    }
    const t2 = t * t;
    const t3 = t2 * t;
    const h10 = t3 - 2 * t2 + t;
    const h01 = -2 * t3 + 3 * t2;
    const h11 = t3 - t2;
    const offset = h10 * h * m0 + h01 * delta + h11 * h * m1;
    const rate = ((6 * t - 6 * t2) * delta) / h + (3 * t2 - 4 * t + 1) * m0 + (3 * t2 - 2 * t) * m1;
    this.write(out, this.cyc[a]!, this.theta[a]! + offset, rate);
    return true;
  }

  /** The speed (rad/s) and acceleration (rad/s²) at the newest record, set by endMotion(). */
  private endRate = 0;
  private endAccel = 0;

  /**
   * The motion at the newest record from the last two chords: their slopes sit
   * at the middles of their segments, so the acceleration is their difference
   * over the distance between the middles, and the speed at the newest record is
   * the last slope carried half a segment on.
   */
  private endMotion(): void {
    const n = this.count;
    this.endRate = 0;
    this.endAccel = 0;
    if (n < 2) return;
    const r1 = this.segmentRate(n - 2);
    const h1 = (this.voice[this.at(n - 1)]! - this.voice[this.at(n - 2)]!) / this.sampleRate;
    if (n < 3 || Math.abs(this.step(n - 3)) > JUMP || Math.abs(this.step(n - 2)) > JUMP) {
      this.endRate = r1;
      return;
    }
    const r0 = this.segmentRate(n - 3);
    const h0 = (this.voice[this.at(n - 2)]! - this.voice[this.at(n - 3)]!) / this.sampleRate;
    const acc = Math.min(MAX_ACCEL, Math.max(-MAX_ACCEL, (r1 - r0) / ((h0 + h1) / 2)));
    // A stop stays a stop, and a speed does not change sign within half a segment.
    if (r1 === 0) return;
    const rate = r1 + (acc * h1) / 2;
    this.endRate = rate * r1 < 0 ? 0 : rate;
    this.endAccel = acc;
  }

  /** The unwrapped angle step from record i to record i + 1. */
  private step(i: number): number {
    const a = this.at(i);
    const b = this.at(i + 1);
    return (this.cyc[b]! - this.cyc[a]!) * CYCLE + (this.theta[b]! - this.theta[a]!);
  }

  /** The chord slope from record i to record i + 1, in radians per second. */
  private segmentRate(i: number): number {
    if (i < 0 || i + 1 >= this.count) return 0;
    const delta = this.step(i);
    if (Math.abs(delta) > JUMP) return 0;
    return (delta * this.sampleRate) / (this.voice[this.at(i + 1)]! - this.voice[this.at(i)]!);
  }

  /**
   * The tangent at record j from its neighbours i and k: the slope of the
   * parabola through the three, which for equal spacing is the central
   * difference. Where the two chords differ by more than KINK (a stop, a start,
   * a jump), j is a corner, and the segment being drawn keeps its own chord
   * `own`, so the curve neither overshoots nor runs back.
   */
  private tangent(i: number, j: number, k: number, own: number): number {
    if (Math.abs(this.step(i)) > JUMP || Math.abs(this.step(j)) > JUMP) return own;
    const hi = (this.voice[this.at(j)]! - this.voice[this.at(i)]!) / this.sampleRate;
    const hk = (this.voice[this.at(k)]! - this.voice[this.at(j)]!) / this.sampleRate;
    const si = this.step(i) / hi;
    const sk = this.step(j) / hk;
    if (Math.abs(si - sk) > KINK * Math.max(Math.abs(si), Math.abs(sk))) return own;
    return (si * hk + sk * hi) / (hi + hk);
  }

  private write(out: TrackSample, cycle: number, theta: number, rate: number): void {
    const wraps = Math.floor(theta / CYCLE);
    out.cycle = cycle + wraps;
    out.theta = theta - wraps * CYCLE;
    out.rate = rate;
  }

  /** The ring index of the i-th oldest record. */
  private at(i: number): number {
    return (this.head + i) % CAPACITY;
  }

  /** The index (from the oldest) of the newest record whose `field` is at most x, or -1. */
  private lastAtOrBefore(field: Float64Array, x: number): number {
    let lo = 0;
    let hi = this.count - 1;
    if (field[this.at(0)]! > x) return -1;
    while (lo < hi) {
      const mid = (lo + hi + 1) >> 1;
      if (field[this.at(mid)]! <= x) lo = mid;
      else hi = mid - 1;
    }
    return lo;
  }
}
